// src/pages/admin/branches/hooks/useBranches.js

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { branchAPI } from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';

export const useBranches = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // State
    const [branches, setBranches] = useState([])
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState(null);
    const [privilegeError, setPrivilegeError] = useState(false);

    // Pagination State
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false
    });

    // Filter state
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        sortBy: 'name',
        sortOrder: 'asc',
    });

    // Permissions
    const permissions = {
        canView: isSuperAdmin || user?.privileges?.some(p => p.module === 'branches' && p.operations.includes('view')),
        canAdd: isSuperAdmin || user?.privileges?.some(p => p.module === 'branches' && p.operations.includes('create')),
        canEdit: isSuperAdmin || user?.privileges?.some(p => p.module === 'branches' && p.operations.includes('update')),
        canDelete: isSuperAdmin || user?.privileges?.some(p => p.module === 'branches' && p.operations.includes('delete')),
    };

    // Check if any filter is active
    const hasActiveFilters = filters.search || filters.status !== 'all';

    // Validation schema
    const branchSchema = Yup.object().shape({
        name: Yup.string()
            .required(t('branches.validation.nameRequired', 'Branch name is required'))
            .min(2, t('branches.validation.nameMin', 'Name must be at least 2 characters'))
            .max(100, t('branches.validation.nameMax', 'Name cannot exceed 100 characters')),
        location: Yup.string()
            .required(t('branches.validation.locationRequired', 'Location is required')),
        address: Yup.string()
            .max(200, t('branches.validation.addressMax', 'Address cannot exceed 200 characters')),
        phone: Yup.string()
            .matches(
                /^\+20[0-9]{10}$/,
                t('branches.validation.phoneInvalid', 'Phone number must be in the format +20 followed by 10 digits')
            ),
        manager: Yup.string()
            .max(100, t('branches.validation.managerMax', 'Manager name cannot exceed 100 characters')),
        isActive: Yup.boolean(),
    });

    // Form initial values
    const getInitialValues = () => {
        if (selectedBranch) {
            return {
                name: selectedBranch.name || '',
                location: selectedBranch.location || '',
                address: selectedBranch.address || '',
                phone: selectedBranch.phone || '',
                manager: selectedBranch.manager || '',
                isActive: selectedBranch.isActive !== undefined ? selectedBranch.isActive : true,
            };
        }
        return {
            name: '',
            location: '',
            address: '',
            phone: '',
            manager: '',
            isActive: true,
        };
    };

    // Formik setup
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: getInitialValues(),
        validationSchema: branchSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                setLoading(true);

                if (selectedBranch) {
                    await branchAPI.update(selectedBranch._id, values);
                    toast.success(t('branches.updateSuccess', 'Branch updated successfully'));
                } else {
                    await branchAPI.create(values);
                    toast.success(t('branches.createSuccess', 'Branch created successfully'));
                }

                handleCloseDialog();
                fetchBranches();
                resetForm();
            } catch (err) {
                toast.error(err.response?.data?.message || t('branches.error', 'An error occurred'));
            } finally {
                setLoading(false);
            }
        },
    });

    // Fetch branches with filters
    const fetchBranches = useCallback(async () => {
        if (!permissions.canView) return;

        setLoading(true);
        setPrivilegeError(false);

        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sort: filters.sortOrder === 'desc' ? `-${filters.sortBy}` : filters.sortBy,
            };

            if (filters.search) {
                params.search = filters.search;
            }

            if (filters.status !== 'all') {
                params.isActive = filters.status === 'active';
            }

            const response = await branchAPI.getAll(params);
            const responseData = response?.data;
            const list = Array.isArray(responseData?.data) ? responseData.data : [];
            const paginationInfo = responseData?.pagination;

            setBranches(list);
            setPagination(prev => ({
                ...prev,
                total: paginationInfo?.total || list.length,
                totalPages: paginationInfo?.pages || 1,
                hasNextPage: paginationInfo?.hasNextPage || false,
                hasPrevPage: paginationInfo?.hasPrevPage || false
            }));

        } catch (err) {
            const backendMessage = err.response?.data?.message;
            if (backendMessage && backendMessage.toLowerCase().includes('insufficient privileges')) {
                setPrivilegeError(true);
            } else {
                setBranches([]);
                toast.error(backendMessage || t('branches.fetchError', 'Failed to fetch branches'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters, permissions.canView, t]);

    // Initial fetch and filter changes
    useEffect(() => {
        fetchBranches();
    }, [fetchBranches]);

    // Dialog handlers
    const handleOpenDialog = (branch = null) => {
        setSelectedBranch(branch);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedBranch(null);
        formik.resetForm();
    };

    // Delete handlers
    const handleDeleteClick = (branch) => {
        setBranchToDelete(branch);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = async (id) => {
        if (!id) return;

        try {
            setLoading(true);
            await branchAPI.delete(id);
            toast.success(t('branches.deleteSuccess', 'Branch deleted successfully'));
            setDeleteConfirmOpen(false);
            setBranchToDelete(null);

            // If deleting the last item on the page, go back one page
            if (branches.length === 1 && pagination.page > 1) {
                setPagination(prev => ({ ...prev, page: prev.page - 1 }));
            } else {
                fetchBranches();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t('branches.deleteError', 'Failed to delete branch'));
        } finally {
            setLoading(false);
        }
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (event) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(event.target.value, 10),
            page: 1
        }));
    };

    // Filter handlers
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchChange = (value) => {
        handleFilterChange('search', value);
    };

    const handleSortChange = (sortBy, sortOrder) => {
        setFilters(prev => ({ ...prev, sortBy, sortOrder }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            status: 'all',
            sortBy: 'name',
            sortOrder: 'asc',
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Calculate statistics
    const getStats = () => {
        const active = branches.filter(b => b.isActive).length;
        const inactive = branches.filter(b => !b.isActive).length;
        return {
            total: pagination.total,
            active,
            inactive
        };
    };

    return {
        // State
        branches,
        loading,
        openDialog,
        selectedBranch,
        deleteConfirmOpen,
        branchToDelete,
        privilegeError,
        permissions,
        pagination,
        filters,
        hasActiveFilters,

        // Form
        formik,

        // Stats
        getStats,

        // Dialog handlers
        handleOpenDialog,
        handleCloseDialog,

        // Delete handlers
        handleDelete,
        handleDeleteClick,
        setDeleteConfirmOpen,

        // Pagination handlers
        handlePageChange,
        handleLimitChange,

        // Filter handlers
        handleFilterChange,
        handleSearchChange,
        handleSortChange,
        clearFilters,

        // Refetch
        fetchBranches,
    };
};